import { useState, useEffect, useMemo } from "react";

// ─── Tooltip contextuel ───────────────────────────────────────────────────────
function HelpTooltip({ content }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen(!open)}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 18, height: 18, borderRadius: "50%",
          background: "#dbeafe", color: "#1d4ed8",
          fontSize: 11, fontWeight: 800, cursor: "help",
          border: "1.5px solid #bfdbfe", marginLeft: 6
        }}
      >?</span>
      {open && (
        <div style={{
          position: "absolute", left: "50%", transform: "translateX(-50%)",
          bottom: 26, background: "#1e293b", color: "#e2e8f0",
          borderRadius: 10, padding: "10px 14px", fontSize: 12,
          width: 240, zIndex: 999, boxShadow: "0 8px 32px #0008",
          lineHeight: 1.6, pointerEvents: "none"
        }}>
          {content}
          <div style={{ position: "absolute", bottom: -6, left: "50%", transform: "translateX(-50%)",
            width: 12, height: 12, background: "#1e293b", rotate: "45deg" }} />
        </div>
      )}
    </span>
  );
}

// ─── Sections du guide ────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "donnees_projet",
    titre: "Données Projet — Le Cadre Stratégique",
    icone: "🏗️",
    badge: "Étape 1",
    couleur: "#2563eb",
    contenu: (
      <div style={{ fontSize: 13, lineHeight: 1.8, color: "#cbd5e1" }}>
        <p style={{ color: "#93c5fd", fontWeight: 700, marginBottom: 8 }}>
          Pourquoi cette étape est fondamentale ?
        </p>
        <p>
          Les informations saisies ici définissent <strong>l'enveloppe complète du projet</strong> :
          nature de l'ouvrage, dimensions, charges et contexte réglementaire. Toutes les analyses
          ultérieures (terrain, solutions, coûts) en dépendent directement.
        </p>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #2563eb" }}>
          <div style={{ color: "#60a5fa", fontWeight: 700, marginBottom: 6 }}>📋 Identification du projet</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Maître d'Ouvrage :</strong> nom du client ou de l'entreprise — apparaît sur le rapport PDF</li>
            <li><strong>Code Référence :</strong> généré automatiquement au format <code style={{ background: "#1e3a5f", padding: "1px 5px", borderRadius: 3 }}>GEO_VILLE_MM/AA_NN_V01</code></li>
            <li><strong>Ville :</strong> sélection dans la liste → détecte automatiquement la zone sismique EC8</li>
          </ul>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #16a34a" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 6 }}>🏛️ Type d'ouvrage</div>
          <p style={{ margin: "0 0 8px" }}>Le type d'ouvrage oriente l'ensemble de l'analyse :</p>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Mur de soutènement</strong> — ouvrage rigide retenant des terres, dimensionné en stabilité globale et cisaillement à la base</li>
            <li><strong>Talus / Remblai</strong> — ouvrage en terre compactée, analyse de glissement circulaire (Bishop)</li>
            <li><strong>Fondation</strong> — semelle isolée ou filante, vérification capacité portante et tassement</li>
            <li><strong>Excavation / Blindage</strong> — soutènement provisoire ou définitif, prise en compte des poussées actives</li>
          </ul>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #d97706" }}>
          <div style={{ color: "#fbbf24", fontWeight: 700, marginBottom: 6 }}>📐 Dimensions et charges</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Hauteur H (m) :</strong> hauteur totale de l'ouvrage depuis le pied jusqu'à la crête. C'est le paramètre le plus sensible — une erreur de 20% sur H peut changer la solution recommandée.</li>
            <li><strong>Longueur L (m) :</strong> développement linéaire de l'ouvrage, utilisé pour le calcul des coûts totaux</li>
            <li><strong>Charges en tête (kN/m) :</strong> surcharges d'exploitation sur le remblai derrière l'ouvrage (véhicules, stockage, bâtiments). Valeur standard pour une route : <strong>10 kN/m²</strong></li>
            <li><strong>Niveaux de sous-sol :</strong> pour les bâtiments, chaque niveau ajoute des charges permanentes et augmente la hauteur d'excavation nécessaire</li>
          </ul>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #7c3aed" }}>
          <div style={{ color: "#a78bfa", fontWeight: 700, marginBottom: 6 }}>🌍 Zone sismique & contexte réglementaire</div>
          <p style={{ margin: "0 0 8px" }}>
            La France est découpée en <strong>5 zones sismiques</strong> (EC8 / NF EN 1998-1). La zone est détectée
            automatiquement à partir de la ville — elle conditionne les vérifications parasismiques obligatoires :
          </p>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Zones 1–2</strong> (faible à modérée) : règles standard EC7, vérification EC8 recommandée</li>
            <li><strong>Zones 3–4</strong> (modérée à forte) : vérifications EC8 obligatoires, coefficients majorés</li>
            <li><strong>Zone 5</strong> (DOM) : sismicité forte, règles spécifiques NF EN 1998-5</li>
          </ul>
        </div>

        <div style={{ background: "#1a2c1a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #4ade80" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 4 }}>💡 Conseils pratiques</div>
          <ul style={{ paddingLeft: 18, margin: 0, color: "#86efac" }}>
            <li>Renseignez la ville réelle du projet — la zone sismique est un paramètre réglementaire, pas estimatif</li>
            <li>Pour la hauteur, mesurez depuis le pied de l'ouvrage (terrain naturel côté aval), pas depuis les fondations</li>
            <li>En l'absence de données de charges précises, utilisez 10 kN/m² pour une route, 20 kN/m² pour un parking</li>
            <li>Le budget géotechnique renseigné sert à filtrer les solutions et à calculer le ROI de l'étude complète</li>
          </ul>
        </div>

        <div style={{ background: "#2a1a1a", borderRadius: 10, padding: "12px 16px", margin: "14px 0", borderLeft: "3px solid #f87171" }}>
          <div style={{ color: "#f87171", fontWeight: 700, marginBottom: 4 }}>⚠️ Points de vigilance</div>
          <ul style={{ paddingLeft: 18, margin: 0, color: "#fca5a5" }}>
            <li>Ne confondez pas la <em>hauteur de l'ouvrage</em> et la <em>profondeur d'encastrement</em> des fondations</li>
            <li>La longueur L n'affecte pas les vérifications structurelles (calculées par mètre linéaire) mais impacte fortement le coût total</li>
            <li>Pour un bâtiment voisin, renseignez la distance dans la page Terrain (Étape 2) — elle génère des contraintes d'espace automatiquement</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "donnees_terrain",
    titre: "Données Terrain — La Coupe Géologique",
    icone: "🪨",
    badge: "Étape 2",
    couleur: "#16a34a",
    contenu: (
      <div style={{ fontSize: 13, lineHeight: 1.8, color: "#cbd5e1" }}>
        <p style={{ color: "#4ade80", fontWeight: 700, marginBottom: 8 }}>
          Décrire le sol en couches successives
        </p>
        <p>
          La coupe géologique est le cœur de l'analyse. Chaque couche est caractérisée par sa
          <strong> nature</strong>, ses dimensions et ses paramètres mécaniques.
        </p>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #16a34a" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 6 }}>📊 Paramètres de chaque couche</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Nature :</strong> type de sol (argile molle/ferme/raide, sable lâche/dense, calcaire, roche…)</li>
            <li><strong>Épaisseur (m) :</strong> hauteur géologique de la couche depuis son toit — ne pas confondre avec la profondeur de fondation</li>
            <li><strong>γ — Poids volumique (kN/m³) :</strong> masse du sol par unité de volume. Argile ~17, Sable ~19, Gravier ~21</li>
            <li><strong>c — Cohésion (kPa) :</strong> résistance intrinsèque du sol. Nulle pour les sables et graviers</li>
            <li><strong>φ — Angle de frottement (°) :</strong> résistance par frottement entre grains. Argile : 15–25°, Sable : 28–38°</li>
          </ul>
          <div style={{ margin: "10px 0 0", padding: "10px 12px", background: "#0a1e38", borderRadius: 8, borderLeft: "3px solid #f87171", fontSize: 12 }}>
            <span style={{ color: "#f87171", fontWeight: 700 }}>⚠️ Épaisseur ≠ Profondeur d'encastrement D</span>
            <span style={{ color: "#fca5a5" }}> — L'épaisseur est une donnée géologique (description du sol).
            La profondeur d'encastrement <em>D</em> est la distance depuis la surface jusqu'à la base de votre fondation.
            C'est ce paramètre <em>D</em> — saisi en bas de page — qui est utilisé pour le calcul de la portance.</span>
          </div>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #0ea5e9" }}>
          <div style={{ color: "#38bdf8", fontWeight: 700, marginBottom: 6 }}>💧 Nappe phréatique</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Absente :</strong> pas d'eau à prendre en compte</li>
            <li><strong>Permanente :</strong> nappe constante — cas le plus défavorable pour la stabilité et la poussée des terres</li>
            <li><strong>Saisonnière :</strong> nappe variable — utiliser la profondeur minimale (cas défavorable)</li>
            <li><strong>Profondeur (m) :</strong> distance depuis la surface au niveau piézométrique de projet</li>
          </ul>
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "#7dd3fc", borderTop: "1px solid #1e3a5f", paddingTop: 8 }}>
            Dès que l'eau est signalée (Saisonnière ou Permanente), saisissez sa profondeur exacte.
            C'est cette donnée qui permet à l'application de calculer la <strong>poussée hydrostatique réelle</strong> sur le parement de votre ouvrage.
          </p>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #d97706" }}>
          <div style={{ color: "#fbbf24", fontWeight: 700, marginBottom: 6 }}>🔬 Essais in-situ (facultatif mais recommandé)</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Pressiomètre (pl*, EM) :</strong> mesure directe de la résistance et du module, référence NF P94-261</li>
            <li><strong>CPT — Pénétromètre statique (qc) :</strong> profil continu de résistance, corrélations qc → φ, c</li>
            <li>En l'absence d'essais, les valeurs estimées sont majorées automatiquement pour conserver une marge de sécurité</li>
          </ul>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #4ade80" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 6 }}>✅ Confirmation de la Résistance (RDT)</div>
          <p style={{ margin: "0 0 8px", color: "#cbd5e1" }}>
            En bas de page, GeoExpert arbitre entre vos paramètres théoriques (<em>c</em>, <em>φ</em>) et vos
            essais réels (<em>p<sub>l</sub>*</em> pressiomètre ou <em>q<sub>c</sub></em> CPT) pour suggérer
            une tranche de résistance admissible.
          </p>
          <ul style={{ paddingLeft: 18, margin: 0, color: "#86efac" }}>
            <li>Validez la tranche suggérée pour <strong>verrouiller l'analyse</strong></li>
            <li>Un badge <strong style={{ color: "#4ade80" }}>"Calculé"</strong> confirme la cohérence entre vos essais et vos paramètres c/φ</li>
            <li>Un badge <strong style={{ color: "#f87171" }}>"Incohérent"</strong> signale un écart important — vérifiez vos saisies avant de continuer</li>
          </ul>
        </div>

        <div style={{ background: "#1a2c1a", borderRadius: 10, padding: "12px 16px", margin: "14px 0", borderLeft: "3px solid #4ade80" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 4 }}>💡 Conseils</div>
          <ul style={{ paddingLeft: 18, margin: 0, color: "#86efac" }}>
            <li>Commencez par la couche en surface (remblai ou terre végétale si présente)</li>
            <li>La couche d'assise (la plus profonde) détermine la résistance admissible et le choix fondamental entre semelles et pieux</li>
            <li>Si vous n'avez pas de rapport de sol, utilisez les valeurs typiques proposées automatiquement par la sélection de nature</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "contraintes",
    titre: "Contraintes & Logistique",
    icone: "⚙️",
    badge: "Étape 3",
    couleur: "#d97706",
    contenu: (
      <div style={{ fontSize: 13, lineHeight: 1.8, color: "#cbd5e1" }}>
        <p style={{ color: "#fbbf24", fontWeight: 700, marginBottom: 8 }}>
          Affiner les solutions selon le contexte chantier
        </p>
        <p>
          Les contraintes logistiques et économiques filtrent les solutions techniquement valides
          pour ne garder que celles qui sont <strong>réellement réalisables</strong> sur votre chantier.
        </p>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #d97706" }}>
          <div style={{ color: "#fbbf24", fontWeight: 700, marginBottom: 6 }}>🏗️ Espace disponible</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Large :</strong> espace illimité côté talus — toutes solutions possibles</li>
            <li><strong>Normal :</strong> 5–10 m disponibles — talutage possible avec emprise raisonnable</li>
            <li><strong>Limité :</strong> 2–5 m — murs ou pieux préférés au talutage</li>
            <li><strong>Contraint :</strong> moins de 2 m — solutions verticales obligatoires (écrans, micropieux)</li>
          </ul>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "#94a3b8" }}>
            💡 Si vous avez renseigné la distance à un bâtiment voisin en Étape 2, l'espace est détecté automatiquement.
          </p>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #7c3aed" }}>
          <div style={{ color: "#a78bfa", fontWeight: 700, marginBottom: 6 }}>⚖️ Priorité du projet</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Sécurité maximale :</strong> solutions robustes, marges importantes — recommandé en zone sismique 3–5 et pour ouvrages de catégorie géotechnique 3</li>
            <li><strong>Équilibre coût/sécurité :</strong> compromis optimal — cas standard de la majorité des projets</li>
            <li><strong>Coût minimal :</strong> solution économique — à utiliser uniquement pour ouvrages temporaires ou de faible enjeu</li>
          </ul>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #16a34a" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 6 }}>💰 Pilotage du Budget</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Coûts indicatifs :</strong> GeoExpert calcule un coût total basé sur la longueur <em>L</em> de votre ouvrage et les ratios moyens du marché (€/ml)</li>
            <li><strong>Personnalisation :</strong> utilisez le bouton d'ajustement dans l'onglet Comparaison pour saisir vos propres prix unitaires — l'application recalcule instantanément la comparaison Coût/Sécurité</li>
            <li><strong>Impact de l'eau :</strong> la présence d'une nappe phréatique génère automatiquement des surcoûts liés au drainage et aux sujétions d'exécution</li>
          </ul>
        </div>

        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #0ea5e9" }}>
          <div style={{ color: "#38bdf8", fontWeight: 700, marginBottom: 6 }}>⏳ Délais de réalisation</div>
          <p style={{ margin: "0 0 8px", color: "#cbd5e1" }}>
            Le choix du délai permet à l'application de pénaliser les solutions aux processus complexes
            (forage, bétonnage lourd) au profit de solutions préfabriquées ou mécanisées si l'urgence l'exige.
          </p>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Normal :</strong> tous types de solutions éligibles — délai standard de mise en œuvre</li>
            <li><strong>Rapide :</strong> favorise les systèmes préfabriqués (gabions, palplanches) et les techniques mécanisées</li>
            <li><strong>Urgent :</strong> filtre strict — uniquement les solutions déployables en quelques jours (palplanches, micropieux rapides)</li>
          </ul>
        </div>

        <div style={{ background: "#2a1a1a", borderRadius: 10, padding: "12px 16px", margin: "14px 0", borderLeft: "3px solid #f87171" }}>
          <div style={{ color: "#f87171", fontWeight: 700, marginBottom: 4 }}>⚠️ Vigilance</div>
          <ul style={{ paddingLeft: 18, margin: 0, color: "#fca5a5" }}>
            <li>Ne choisissez jamais "Coût minimal" pour un ouvrage en zone sismique 3–5 sans validation par un ingénieur</li>
            <li>L'espace disponible auto-détecté depuis la distance bâtiment est conservateur — vous pouvez le corriger manuellement</li>
          </ul>
        </div>

        <div style={{ background: "#1a2c1a", borderRadius: 10, padding: "12px 16px", margin: "14px 0", borderLeft: "3px solid #4ade80" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 4 }}>💡 Conseil d'expert</div>
          <p style={{ margin: 0, color: "#86efac" }}>
            Si vous hésitez sur la priorité, gardez le mode <strong>"Équilibre"</strong>. C'est le mode qui garantit
            la meilleure longévité de l'ouvrage au prix le plus juste, conformément à l'approche optimisation
            coût/fiabilité de l'<strong>Eurocode 7</strong>.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "resultats",
    titre: "Résultats & Choix de Solutions — Le Verdict Expert",
    icone: "📊",
    badge: "Analyse",
    couleur: "#0ea5e9",
    contenu: (
      <div style={{ fontSize: 13, lineHeight: 1.8, color: "#cbd5e1" }}>
        <p style={{ color: "#38bdf8", fontWeight: 700, marginBottom: 8 }}>
          Interpréter et exploiter les résultats de l'analyse
        </p>

        {/* Solution recommandée */}
        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #0ea5e9" }}>
          <div style={{ color: "#38bdf8", fontWeight: 700, marginBottom: 6 }}>🥇 Solution Recommandée</div>
          <p style={{ margin: "0 0 10px" }}>
            La solution affichée en premier est celle qui obtient le <strong>meilleur score global</strong>
            en combinant sécurité structurelle, faisabilité chantier et adéquation économique selon votre priorité déclarée.
          </p>

          <div style={{ background: "#0a1e38", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
            <div style={{ color: "#60a5fa", fontWeight: 700, marginBottom: 4 }}>Adéquation (%)</div>
            <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
              Ce score n'est pas qu'une statistique — c'est une <strong style={{ color: "#cbd5e1" }}>moyenne pondérée</strong> calculée
              par le moteur de règles expert. Il intègre :
            </p>
            <ul style={{ paddingLeft: 16, margin: "6px 0 0", fontSize: 12, color: "#94a3b8" }}>
              <li><strong style={{ color: "#cbd5e1" }}>Stabilité Technique</strong> — respect des coefficients de sécurité Eurocode 7</li>
              <li><strong style={{ color: "#cbd5e1" }}>Emprise de Chantier</strong> — compatibilité avec la distance des bâtiments voisins</li>
              <li><strong style={{ color: "#cbd5e1" }}>Optimisation Économique</strong> — plus le coût est bas à sécurité équivalente, plus le score grimpe</li>
            </ul>
            <p style={{ margin: "8px 0 0", fontSize: 12, color: "#4ade80" }}>
              Un score &gt; 90 % indique une solution idéale pour votre contexte.
            </p>
          </div>

          <div style={{ background: "#1a200a", borderRadius: 8, padding: "10px 12px", marginBottom: 8, borderLeft: "3px solid #f59e0b" }}>
            <div style={{ color: "#fbbf24", fontWeight: 700, marginBottom: 4 }}>⚠️ Points d'Attention — L'IA Critique</div>
            <p style={{ margin: 0, fontSize: 12, color: "#fde68a" }}>
              Ne les ignorez pas. C'est ici que l'IA exprime son <strong>"esprit critique"</strong> : elle peut valider une solution
              techniquement tout en vous alertant sur une difficulté d'exécution spécifique
              (ex : <em>"Attention : la proximité du bâtiment voisin complique le ferraillage du talon"</em>).
              Ces alertes soulignent les risques d'exécution ou les contraintes réglementaires à anticiper
              (ex : surveillance inclinométrique obligatoire).
            </p>
          </div>

          <div style={{ background: "#0a1e38", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ color: "#a78bfa", fontWeight: 700, marginBottom: 4 }}>🔗 Le Pont vers GEO5</div>
            <p style={{ margin: 0, fontSize: 12, color: "#c4b5fd" }}>
              GeoExpert est votre boussole de pré-dimensionnement. Une fois la solution choisie, les badges affichés
              (ex : <strong>GEO5 Gabions</strong>, <strong>GEO5 Murs</strong>) vous indiquent précisément quel module
              lancer pour finaliser votre note de calcul officielle.
              C'est le passage du <em>Diagnostic</em> à l'<em>Ingénierie de Précision</em>.
            </p>
          </div>
        </div>

        {/* Onglet Comparaison */}
        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", margin: "14px 0", borderLeft: "3px solid #16a34a" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 6 }}>📈 Onglet Comparaison Coût / Sécurité</div>
          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#94a3b8" }}>Votre outil d'arbitrage financier.</p>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Graphique Dynamique :</strong> comparez les coûts totaux (basés sur votre longueur <em>L</em>) avec les niveaux de sécurité de chaque solution</li>
            <li><strong>Éditeur de Prix :</strong> personnalisez les tarifs (€/ml) pour coller à votre réalité locale ou aux devis de vos sous-traitants</li>
            <li><strong>Impact Nappe :</strong> le surcoût lié à la présence d'eau est automatiquement intégré pour éviter les mauvaises surprises budgétaires</li>
          </ul>
        </div>

        {/* Rappel réglementaire */}
        <div style={{ background: "#2a1a1a", borderRadius: 10, padding: "12px 16px", margin: "14px 0", borderLeft: "3px solid #f87171" }}>
          <div style={{ color: "#f87171", fontWeight: 700, marginBottom: 4 }}>⚖️ Rappel Réglementaire</div>
          <p style={{ margin: 0, color: "#fca5a5" }}>
            GeoExpert est un outil de <strong>pré-dimensionnement et d'aide à la décision</strong>. Les résultats
            sont des recommandations d'orientation. Une <strong>étude géotechnique complète (Mission G2 PRO)</strong> réalisée
            par un ingénieur agréé reste indispensable avant tout démarrage de travaux
            (<strong>NF EN 1997-1</strong>, catégorie géotechnique ≥ 2).
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "agent_copilote",
    titre: "L'Analyse IA — Votre Expert Géotechnique Embarqué",
    icone: "🤖",
    badge: "Outils IA",
    couleur: "#7c3aed",
    contenu: (
      <div style={{ fontSize: 13, lineHeight: 1.8, color: "#cbd5e1" }}>

        {/* Intro */}
        <div style={{ background: "linear-gradient(135deg, #1e1040, #0f1e3a)", borderRadius: 10, padding: "14px 16px", marginBottom: 14, border: "1px solid #4c1d95" }}>
          <div style={{ color: "#c4b5fd", fontWeight: 700, marginBottom: 6 }}>🚀 Pourquoi utiliser l'Analyse IA ?</div>
          <p style={{ margin: 0, color: "#ddd6fe" }}>
            L'Analyse IA de GeoExpert est un <strong>système expert proactif</strong>, pas un simple assistant.
            Contrairement à un logiciel de calcul classique qui attend des chiffres pour donner un résultat,
            il <strong>interprète le contexte</strong> : il détecte des incohérences, justifie des choix normatifs
            complexes et propose des stratégies de remédiation — avant même que vous n'ouvriez GEO5.
          </p>
        </div>

        {/* 5 questions */}
        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", marginBottom: 14, borderLeft: "3px solid #7c3aed" }}>
          <div style={{ color: "#a78bfa", fontWeight: 700, marginBottom: 10 }}>🔍 Les 5 Raccourcis d'Expertise — La Puissance en un clic</div>
          <p style={{ margin: "0 0 12px", fontSize: 12, color: "#94a3b8" }}>
            Pour chaque solution analysée, l'Agent propose des questions contextuelles adaptées à votre projet en cours :
          </p>

          {[
            {
              q: `"Analyse la cohérence de mes paramètres de sol (c, φ) par rapport à la nature choisie."`,
              p: `L'IA vérifie si vos saisies pressiométriques ou de laboratoire sont réalistes. Elle vous alerte si vous avez surestimé la cohésion d'une argile molle — évitant ainsi un calcul dangereux.`,
            },
            {
              q: `"Justifie le choix de la solution face aux contraintes de voisinage et de nappe."`,
              p: `L'IA rédige l'argumentaire technique. Elle explique, par exemple, pourquoi la nappe à −1,50 m rend la paroi de pieux sécants préférable au mur cantilever, en citant les risques de poussée hydrostatique.`,
            },
            {
              q: `"Calcule les coefficients sismiques Kh et Kv applicables à ce projet selon l'EC8."`,
              p: `Plus besoin de chercher dans les tableaux. L'IA extrait la zone sismique et la catégorie d'importance pour vous donner les valeurs exactes à injecter dans GEO5.`,
            },
            {
              q: `"Propose 3 variantes d'optimisation si le Facteur de Sécurité au glissement est insuffisant."`,
              p: `Si votre calcul GEO5 échoue, l'IA ne dit pas simplement "non". Elle propose : ajout d'une bêche, élargissement de la base, ou modification de l'encastrement D.`,
            },
            {
              q: `"Rédige la conclusion technique pour mon rapport d'étude G2-AVP."`,
              p: `L'IA compile toutes les données (sol, nappe, séisme, solution) pour générer une synthèse professionnelle prête à être insérée dans votre rapport final.`,
            },
          ].map(({ q, p }, i) => (
            <div key={i} style={{ marginBottom: 10, background: "#080f1e", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ color: "#c4b5fd", fontStyle: "italic", fontSize: 12, marginBottom: 4 }}>{q}</div>
              <div style={{ color: "#94a3b8", fontSize: 11.5 }}>
                <strong style={{ color: "#a78bfa" }}>Puissance : </strong>{p}
              </div>
            </div>
          ))}
        </div>

        {/* Ce qui rend l'IA unique */}
        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", marginBottom: 14, borderLeft: "3px solid #0ea5e9" }}>
          <div style={{ color: "#38bdf8", fontWeight: 700, marginBottom: 6 }}>⚡ Ce qui rend l'Analyse IA unique</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Synthèse Multidimensionnelle :</strong> croise la géologie (Page 2) avec la logistique (Page 3) pour valider la faisabilité réelle</li>
            <li><strong>Vérification Normative :</strong> connaît les spécificités françaises (NF P94-261, NF P94-281) et s'assure que vos hypothèses restent dans le cadre réglementaire</li>
            <li><strong>Gain de Productivité :</strong> transforme 1 heure de recherche documentaire et de rédaction en 10 secondes d'analyse automatisée</li>
          </ul>
        </div>

        {/* Co-Pilote */}
        <div style={{ background: "#0f2a4a", borderRadius: 10, padding: "14px 16px", marginBottom: 14, borderLeft: "3px solid #4ade80" }}>
          <div style={{ color: "#4ade80", fontWeight: 700, marginBottom: 6 }}>🧭 Co-Pilote GEO5 — Module par Module</div>
          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#94a3b8" }}>
            À utiliser en parallèle avec GEO5 ouvert sur votre écran. 24 modules couverts.
          </p>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li>Workflow pas à pas : ordre des onglets dans GEO5, points d'attention</li>
            <li>Valeurs typiques adaptées aux sols français</li>
            <li>Alertes contextuelles EC8 spécifiques à votre zone sismique</li>
            <li>Checklist de validation avant d'accepter les résultats GEO5</li>
          </ul>
        </div>

        {/* Conseil */}
        <div style={{ background: "#1a1030", borderRadius: 10, padding: "12px 16px", borderLeft: "3px solid #a78bfa" }}>
          <div style={{ color: "#c4b5fd", fontWeight: 700, marginBottom: 4 }}>💡 Note aux utilisateurs</div>
          <p style={{ margin: 0, color: "#ddd6fe", fontSize: 12 }}>
            L'Agent dispose d'une <strong>"mémoire de projet"</strong>. Plus vous renseignez de détails en Pages 1 et 2,
            plus son diagnostic devient <strong>chirurgical et percutant</strong>. Démarrez toujours depuis
            la page Solution pour lui transmettre le contexte complet avant de poser vos questions.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "zones_sismiques",
    titre: "Zones Sismiques EC8 — Référence France",
    icone: "🌍",
    badge: "Eurocode 8",
    couleur: "#0ea5e9",
    contenu: (
      <div style={{ fontSize: 13, lineHeight: 1.8, color: "#cbd5e1" }}>
        <p>
          L'EC8 (NF EN 1998-1 + NF EN 1998-5) divise la France en <strong>5 zones sismiques</strong>.
          La zone est détectée automatiquement à partir de la ville saisie en Étape 1.
        </p>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginTop: 12 }}>
          <thead>
            <tr style={{ background: "#1e3a5f", color: "#93c5fd" }}>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Zone</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>kh (NF EN 1998-5 §7.3.2.2)</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Exemples de communes</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["1", "Très faible (kh=0.037)", "Bretagne, Normandie, Île-de-France (hors failles)"],
              ["2", "Faible (kh=0.065)", "Bassin parisien, Centre, Bordeaux"],
              ["3", "Modérée (kh=0.103)", "Pyrénées occidentales, Massif Central nord"],
              ["4", "Moyenne (kh=0.149)", "Alpes nord, Pyrénées centrales, Provence"],
              ["5", "Forte (kh=0.280)", "Guadeloupe, Martinique (DOM)"],
            ].map(([z, ag, ex]) => (
              <tr key={z} style={{ borderBottom: "1px solid #2a3a5f" }}>
                <td style={{ padding: "7px 10px", color: "#fbbf24", fontWeight: 800, fontSize: 15 }}>Z{z}</td>
                <td style={{ padding: "7px 10px", color: "#93c5fd" }}>{ag}</td>
                <td style={{ padding: "7px 10px", fontSize: 11, color: "#94a3b8" }}>{ex}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ background: "#1a2c1a", borderRadius: 10, padding: "12px 16px", marginTop: 14, borderLeft: "3px solid #4ade80" }}>
          <p style={{ margin: 0, color: "#86efac", fontSize: 12 }}>
            💡 Les vérifications parasismiques (NF EN 1998-5 §7) sont obligatoires dès la Zone 2 pour les
            ouvrages de catégorie d'importance II et supérieure. L'application signale automatiquement
            les vérifications requises selon votre zone et type d'ouvrage.
          </p>
        </div>
      </div>
    ),
  },
];

// ─── Mapping contexte → section ──────────────────────────────────────────────
function sectionContextuelle(onglet, etapeProjet) {
  if (onglet === "projet" && etapeProjet === 0) return "donnees_projet";
  if (onglet === "projet" && etapeProjet === 1) return "donnees_terrain";
  if (onglet === "projet" && etapeProjet === 2) return "contraintes";
  if (onglet === "solution") return "resultats";
  if (onglet === "comparaison") return "resultats";
  if (onglet === "agent") return "agent_copilote";
  if (onglet === "copilote") return "agent_copilote";
  return "donnees_projet";
}

// ─── HelpModal — panneau accordéon ───────────────────────────────────────────
function HelpModal({ onClose, onglet, etapeProjet }) {
  const sectionInit = useMemo(
    () => sectionContextuelle(onglet, etapeProjet),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [ouvert, setOuvert] = useState(sectionInit);

  useEffect(() => {
    const sc = sectionContextuelle(onglet, etapeProjet);
    setOuvert(sc);
  }, [onglet, etapeProjet]);

  const toggle = (id) => setOuvert(prev => prev === id ? null : id);

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
        zIndex: 9999,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#0a1628",
        width: "100%", maxWidth: 520,
        height: "100vh",
        display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 40px #0009",
        overflowY: "auto",
        animation: "slideInRight 0.2s ease-out",
      }}>
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(40px); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>

        {/* En-tête */}
        <div style={{
          background: "linear-gradient(135deg, #1d3a6e, #1e40af)",
          padding: "22px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, zIndex: 10,
          borderBottom: "1px solid #2a3a5f",
          flexShrink: 0,
        }}>
          <div>
            <div style={{ color: "#93c5fd", fontSize: 11, letterSpacing: 3, fontWeight: 700, marginBottom: 4 }}>
              GUIDE UTILISATEUR
            </div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>
              GEO5 Expert Advisor
            </div>
            <div style={{ color: "#60a5fa", fontSize: 12, marginTop: 3 }}>
              Aide contextuelle — cliquez une section pour la déplier
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff", width: 38, height: 38, borderRadius: "50%",
              cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Accordéon */}
        <div style={{ padding: "12px 16px 32px", flex: 1 }}>
          {SECTIONS.map((s) => {
            const isOpen = ouvert === s.id;
            return (
              <div key={s.id} style={{
                marginBottom: 10,
                borderRadius: 12,
                border: `1px solid ${isOpen ? s.couleur : "#1e3a5f"}`,
                overflow: "hidden",
                transition: "border-color 0.2s",
              }}>
                {/* Titre section */}
                <button
                  onClick={() => toggle(s.id)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 12,
                    padding: "14px 16px",
                    background: isOpen
                      ? `linear-gradient(90deg, ${s.couleur}22, transparent)`
                      : "#0f1e3a",
                    border: "none", cursor: "pointer",
                    textAlign: "left", transition: "background 0.2s",
                  }}
                >
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{s.icone}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: isOpen ? "#fff" : "#93c5fd", fontWeight: 700, fontSize: 13.5 }}>
                      {s.titre}
                    </div>
                  </div>
                  <span style={{
                    background: isOpen ? s.couleur : "#1e3a5f",
                    color: isOpen ? "#fff" : "#64748b",
                    fontSize: 10, fontWeight: 700, padding: "2px 8px",
                    borderRadius: 20, flexShrink: 0,
                    transition: "all 0.2s",
                  }}>
                    {s.badge}
                  </span>
                  <span style={{
                    color: isOpen ? "#fff" : "#475569",
                    fontSize: 16, flexShrink: 0,
                    transform: isOpen ? "rotate(90deg)" : "none",
                    transition: "transform 0.2s",
                    display: "inline-block",
                  }}>›</span>
                </button>

                {/* Contenu dépliable */}
                {isOpen && (
                  <div style={{
                    padding: "6px 16px 18px",
                    background: "#080f1e",
                    borderTop: `1px solid ${s.couleur}44`,
                  }}>
                    {s.contenu}
                  </div>
                )}
              </div>
            );
          })}

          {/* Pied */}
          <div style={{
            marginTop: 20, padding: "14px 16px",
            background: "#0f1e3a", borderRadius: 10,
            border: "1px solid #1e3a5f",
            fontSize: 11, color: "#475569", textAlign: "center", lineHeight: 1.7,
          }}>
            GEO5 Expert Advisor — CIVISOFT France 2025<br />
            Basé sur EC7 (NF EN 1997), EC8 (NF EN 1998) et NF P94<br />
            <span style={{ color: "#f87171" }}>⚠️ Recommandations d'orientation uniquement — étude géotechnique obligatoire avant travaux</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ONGLET COMPARAISON — constantes partagées (legacy)
// =============================================================================

const PRIX_DZD = {
  beton_c1215: 7000, beton_c2025: 30000, acier: 98,
  coffrage: 580, cage_gabion: 3850, pierre: 2500,
  terrassement: 400, remblai: 1500,
};

const LABELS_SOLUTIONS = {
  murs_cantilever: { nom: "Mur Cantilever BA", icone: "🏗️", couleur: "#2563eb", range: "H opt. 2–6m" },
  murs_poids:      { nom: "Mur Poids Cyclopéen", icone: "🪨", couleur: "#d97706", range: "H opt. 1–4m" },
  gabions:         { nom: "Mur Gabions", icone: "🧱", couleur: "#16a34a", range: "H opt. 1–5m" },
  pentes_clouees:  { nom: "Pentes Clouées", icone: "⚓", couleur: "#7c3aed", range: "H opt. 3–15m" },
};

export { HelpTooltip, HelpModal };
